import { useCallback, useEffect, useMemo, useRef } from "react";
import { AdvancedMarker, Map, Pin, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { logErrorForDev } from "../../../utils";
import type { LocationSelectionResult, LocationSelectionSource } from "../../../utils/location/locationSelection";
import {
    dismissNativePoiInfoWindow,
    retainNativePoiInfoWindowCapture,
} from "../../../utils/location/dismissNativePoiInfo";
import {
    locationSelectionFromGeocoderResultRaw,
    locationSelectionFromGooglePlace,
} from "../../../utils/location/locationSelection";

export interface LocationMapEditorProps {
    mapId: string;
    defaultCenter: { lat: number; lng: number };
    defaultZoom: number;
    defaultMapType: string;
    latitude: number | null | undefined;
    longitude: number | null | undefined;
    disabled?: boolean;
    onCoordinatesChange: (latitude: number, longitude: number) => void;
    onSelectionComplete: (selection: LocationSelectionResult) => void;
    onGeocodeFailed: (latitude: number, longitude: number) => void;
}

/** Pans the map viewport when marker coordinates change (e.g. place search or loaded record). */
function MapFollowMarker({ position }: { position: { lat: number; lng: number } | null }) {
    const map = useMap();

    useEffect(() => {
        if (!map || position == null) return;
        map.panTo(position);
    }, [map, position]);

    return null;
}

function MapClickHandler({
    disabled,
    onMapClick,
}: {
    disabled?: boolean;
    onMapClick: (lat: number, lng: number, placeId: string | null) => void;
}) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const releaseCapture = retainNativePoiInfoWindowCapture();

        const listener = map.addListener(
            "click",
            (event: google.maps.MapMouseEvent & google.maps.IconMouseEvent) => {
                if (disabled) return;

                const latLng = event.latLng;
                if (!latLng) return;

                if (!event.placeId) {
                    dismissNativePoiInfoWindow(map);
                }

                onMapClick(latLng.lat(), latLng.lng(), event.placeId ?? null);
            }
        );

        return () => {
            listener.remove();
            releaseCapture();
        };
    }, [map, disabled, onMapClick]);

    return null;
}

function LocationMapMarker({
    position,
    disabled,
    onDragEnd,
}: {
    position: { lat: number; lng: number };
    disabled?: boolean;
    onDragEnd: (lat: number, lng: number) => void;
}) {
    const map = useMap();

    return (
        <AdvancedMarker
            position={position}
            draggable={!disabled}
            onDragEnd={(event) => {
                if (disabled) return;
                const latLng = event.latLng;
                if (!latLng) return;
                dismissNativePoiInfoWindow(map);
                onDragEnd(latLng.lat(), latLng.lng());
            }}
        >
            <Pin background="var(--primary)" borderColor="#fff" glyphColor="#fff" />
        </AdvancedMarker>
    );
}

const LocationMapEditor = ({
    mapId,
    defaultCenter,
    defaultZoom,
    defaultMapType,
    latitude,
    longitude,
    disabled,
    onCoordinatesChange,
    onSelectionComplete,
    onGeocodeFailed,
}: LocationMapEditorProps) => {
    const geocoding = useMapsLibrary("geocoding");
    const places = useMapsLibrary("places");
    const geocoder = useMemo(() => (geocoding ? new geocoding.Geocoder() : null), [geocoding]);
    const resolveGenerationRef = useRef(0);

    const markerPosition = useMemo(() => {
        if (latitude == null || longitude == null) return null;
        return { lat: latitude, lng: longitude };
    }, [latitude, longitude]);

    const completePostalGeocode = useCallback(
        (
            result: google.maps.GeocoderResult,
            lat: number,
            lng: number,
            source: LocationSelectionSource
        ) => {
            onSelectionComplete(
                locationSelectionFromGeocoderResultRaw({
                    source,
                    result,
                    pinCoords: { latitude: lat, longitude: lng },
                })
            );
        },
        [onSelectionComplete]
    );

    const resolvePlaceByIdRef = useRef<
        (placeId: string, lat: number, lng: number, source: LocationSelectionSource) => void
    >(() => undefined);

    const reverseGeocode = useCallback(
        (lat: number, lng: number, source: LocationSelectionSource, postalOnly = false) => {
            if (!geocoder) {
                onGeocodeFailed(lat, lng);
                return;
            }

            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status !== "OK" || !results?.[0]?.address_components) {
                    onGeocodeFailed(lat, lng);
                    return;
                }

                const result = results[0];

                if (!postalOnly) {
                    const geocoderPlaceId = result.place_id;
                    const isPoiType = (result.types ?? []).some((t) =>
                        ["point_of_interest", "establishment", "store", "food", "health", "lodging"].includes(t)
                    );

                    if (geocoderPlaceId && isPoiType && places?.Place) {
                        resolvePlaceByIdRef.current(geocoderPlaceId, lat, lng, "poi-click");
                        return;
                    }
                }

                completePostalGeocode(result, lat, lng, source);
            });
        },
        [geocoder, onGeocodeFailed, completePostalGeocode, places]
    );

    const resolvePlaceById = useCallback(
        async (placeId: string, lat: number, lng: number, source: LocationSelectionSource) => {
            const generation = ++resolveGenerationRef.current;

            if (!places?.Place) {
                reverseGeocode(lat, lng, source, true);
                return;
            }

            try {
                const place = new places.Place({ id: placeId });
                await place.fetchFields({
                    fields: ["displayName", "formattedAddress", "addressComponents", "location"],
                });

                if (generation !== resolveGenerationRef.current) return;

                const selection = locationSelectionFromGooglePlace(source, place, {
                    latitude: lat,
                    longitude: lng,
                });
                if (selection) {
                    onSelectionComplete(selection);
                    return;
                }
            } catch (e: unknown) {
                logErrorForDev("LocationMapEditor.resolvePlaceById", e);
            }

            if (generation === resolveGenerationRef.current) {
                reverseGeocode(lat, lng, source, true);
            }
        },
        [places, onSelectionComplete, reverseGeocode]
    );

    resolvePlaceByIdRef.current = (placeId, lat, lng, source) => {
        void resolvePlaceById(placeId, lat, lng, source);
    };

    const handleMapInteraction = useCallback(
        (lat: number, lng: number, placeId: string | null | undefined, source: LocationSelectionSource) => {
            onCoordinatesChange(lat, lng);

            const resolvedSource: LocationSelectionSource = placeId ? "poi-click" : source;

            if (placeId) {
                void resolvePlaceById(placeId, lat, lng, resolvedSource);
                return;
            }

            reverseGeocode(lat, lng, resolvedSource === "poi-click" ? "map-click" : resolvedSource);
        },
        [onCoordinatesChange, resolvePlaceById, reverseGeocode]
    );

    const initialMapType =
        defaultMapType === "satellite" || defaultMapType === "hybrid" ? "satellite" : "roadmap";

    return (
        <div className="position-relative border rounded overflow-hidden" style={{ height: 360 }}>
            <Map
                mapId={mapId}
                defaultCenter={defaultCenter}
                defaultZoom={defaultZoom}
                mapTypeId={initialMapType}
                gestureHandling={disabled ? "none" : "greedy"}
                disableDefaultUI={false}
                clickableIcons={true}
                style={{ width: "100%", height: "100%" }}
            >
                <MapFollowMarker position={markerPosition} />
                <MapClickHandler
                    disabled={disabled}
                    onMapClick={(lat, lng, placeId) =>
                        handleMapInteraction(lat, lng, placeId, "map-click")
                    }
                />
                {markerPosition && (
                    <LocationMapMarker
                        position={markerPosition}
                        disabled={disabled}
                        onDragEnd={(lat, lng) => handleMapInteraction(lat, lng, null, "marker-drag")}
                    />
                )}
            </Map>
        </div>
    );
};

export { LocationMapEditor };
